// ═══════════════════════════════════════════════════════════════════════════════
// MoonbeamCreatorRecipients.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// Step 1 of the send flow: "How to Share". Lets users configure one or both
// of two parallel share channels:
//
//   🔗 Get a link to share yourself (TOP, toggle off by default)
//   ✉️ Let {siteName} email it for you (BOTTOM, toggle off by default)
//
// CHANGES IN THIS REVISION (Share button + link-only fast path):
//   - The Step 1 action button is now "Share" (was "Review & Share").
//   - Branching behavior on click:
//       • LINK ONLY (link on, email off / no recipients) → call
//         onShareDirect() to run the real send immediately and SKIP the
//         review screen. The user lands on My Moonbeams with the
//         "link copied" modal.
//       • EMAIL involved (email on with recipients, with or without a
//         link) → call onNext() to go to the review screen (Step 2), as
//         before. The modal then appears on My Moonbeams after they
//         confirm on Step 2.
//   - New props: onShareDirect, sending. The button shows "Sharing..." and
//     is disabled while a direct (link-only) send is in flight.
//   - Display text "eCard"→"Moonbeam" in user-facing strings. Routes,
//     fields, recipientKey, and variables unchanged.
//
// PREVIOUS REVISIONS — preserved:
//   - Simple email form (per-tier) vs full recipient manager.
//   - Link "note" UX, section restructure, email toggle soft-preserve.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api.js';
import contactService from '../../services/contactService.js';
import { isValidEmail } from '../../utils/validators.js';
import {
  CONTACTS_PER_PAGE, MONTHS, TIMEZONE_REGION_ORDER,
  daysInMonth, formatBirthday, isUnlimitedUsage,
  UpgradeBanner, UsageStats,
} from './MoonbeamCreatorShared.js';
import { ScheduleSendModal, BirthdayTimeModal } from './MoonbeamCreatorModals.js';

// ─── Recipients Review Modal ──────────────────────────────────────────────────

function RecipientsReviewModal({ recipients, onRemove, onClearAll, isBirthdayDelivery, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col sm:mx-4">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">{recipients.length} Recipient{recipients.length !== 1 ? 's' : ''}</h2>
            <p className="text-royal-100 text-sm">Review your recipient list</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gold transition-colors text-xl p-2">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {recipients.length === 0 ? (
            <p className="text-royal-100 text-base text-center py-10">No recipients added yet.</p>
          ) : (
            recipients.map((r, idx) => {
              const key = r.recipientKey || `manual-${r.email}-${r.name}`;
              return (
                <div key={`${key}-${idx}`} className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-royal-800/40 last:border-0">
                  <div className="w-9 h-9 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-gold text-sm font-bold">{(r.name || r.email)[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-base truncate">{r.name}</p>
                    <p className="text-royal-100 text-sm truncate">
                      {r.email}
                      {isBirthdayDelivery && r.birthdayDisplay && <span className="ml-1">🎂 {r.birthdayDisplay}</span>}
                    </p>
                  </div>
                  <span className="text-sm text-royal-100 px-2 py-0.5 rounded bg-royal-800 hidden sm:inline flex-shrink-0">
                    {r.source || 'Manual'}
                  </span>
                  <button onClick={() => onRemove(key)} className="text-royal-100 hover:text-red-400 transition-colors flex-shrink-0 p-1.5">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-royal-700/50 flex-shrink-0">
          {recipients.length > 0 && (
            <button onClick={onClearAll} className="text-sm text-royal-100 hover:text-red-400 transition-colors">
              Clear All
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Contact Modal (Enhanced with Household Support) ──────────────────────

function AddContactModal({ onAdd, isBirthdayDelivery, atLimit, onClose, onRefreshContacts, tier }) {
  const [mode, setMode] = useState('adult');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [birthmonth, setBirthmonth] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [timezone, setTimezone] = useState('');
  const [saveToContacts, setSaveToContacts] = useState(true);
  const [addAsRecipient, setAddAsRecipient] = useState(true);

  const [hmFirstname, setHmFirstname] = useState('');
  const [hmBirthmonth, setHmBirthmonth] = useState('');
  const [hmBirthdate, setHmBirthdate] = useState('');
  const [hmNotes, setHmNotes] = useState('');

  const [selectedParent, setSelectedParent] = useState(null);
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState([]);
  const [parentSearchLoading, setParentSearchLoading] = useState(false);

  const [newParentName, setNewParentName] = useState('');
  const [newParentEmail, setNewParentEmail] = useState('');

  const [error, setError] = useState('');
  const [justAdded, setJustAdded] = useState(null);
  const [timezoneGrouped, setTimezoneGrouped] = useState({});
  const [saving, setSaving] = useState(false);

  const [duplicateContact, setDuplicateContact] = useState(null);

  const maxDay = daysInMonth(birthmonth ? parseInt(birthmonth) : null);
  const hmMaxDay = daysInMonth(hmBirthmonth ? parseInt(hmBirthmonth) : null);

  useEffect(() => {
    api.get('/contacts/timezones', false)
      .then((data) => setTimezoneGrouped(data.grouped || {}))
      .catch(() => {});
  }, []);

  const searchParents = useCallback(async (term) => {
    if (!term || term.length < 2) { setParentResults([]); return; }
    setParentSearchLoading(true);
    try {
      const params = new URLSearchParams({ search: term, limit: 10, page: 1 });
      const data = await contactService.getContacts(params.toString());
      setParentResults(data.contacts || []);
    } catch { setParentResults([]); }
    setParentSearchLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchParents(parentSearch), 300);
    return () => clearTimeout(timer);
  }, [parentSearch, searchParents]);

  const handleAddAdult = async () => {
    setError('');
    setJustAdded(null);

    if (!addAsRecipient && !saveToContacts) { setError('Select at least one action'); return false; }
    if (!name.trim()) { setError('Name is required'); return false; }
    if (!email.trim()) { setError('Email is required'); return false; }
    const emailValid = typeof isValidEmail === 'function' ? isValidEmail(email) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailValid) { setError('Please enter a valid email'); return false; }
    if (isBirthdayDelivery && addAsRecipient && (!birthmonth || !birthdate)) { setError('Birthday is required for birthday delivery'); return false; }
    if (addAsRecipient && atLimit) { setError('Recipient limit reached.'); return false; }

    const bm = birthmonth ? parseInt(birthmonth) : null;
    const bd = birthdate ? parseInt(birthdate) : null;
    const nameParts = name.trim().split(/\s+/);
    const firstname = nameParts[0];
    const lastname = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

    let contactid = null;

    if (saveToContacts) {
      setSaving(true);
      try {
        const resp = await api.post('/contacts', {
          email: email.toLowerCase().trim(), firstname, lastname,
          birthmonth: bm, birthdate: bd, timezone: timezone || null,
        });
        if (resp.contact) contactid = resp.contact.contactid;
      } catch (err) {
        if (err.status === 400 || err.status === 409) {
          try {
            const searchResp = await contactService.getContacts(`search=${encodeURIComponent(email.toLowerCase().trim())}&limit=1`);
            const existing = (searchResp.contacts || []).find(c => c.email.toLowerCase() === email.toLowerCase().trim());
            if (existing) {
              const existingName = [existing.firstname, existing.lastname].filter(Boolean).join(' ') || existing.email;
              setDuplicateContact({ contactid: existing.contactid, name: existingName, email: existing.email });
              setMode('duplicate-prompt');
              setSaving(false);
              return false;
            }
          } catch { /* fall through */ }
          setError('A contact with this email already exists.');
          setSaving(false);
          return false;
        }
        console.error('Failed to save contact:', err);
      }
      setSaving(false);
    }

    if (addAsRecipient) {
      const recipientKey = contactid ? `contact-${contactid}` : `manual-${email.toLowerCase().trim()}-${name.trim()}`;
      const added = onAdd({
        name: name.trim(), email: email.toLowerCase().trim(), contactid,
        birthmonth: bm, birthdate: bd, birthdayDisplay: formatBirthday(bm, bd),
        timezone: timezone || null, source: saveToContacts ? 'Contact' : 'Manual', recipientKey,
      });
      if (added === false) { setError('This recipient is already added'); return false; }
    }

    const action = addAsRecipient && saveToContacts ? 'Added & saved' : addAsRecipient ? 'Added' : 'Saved to contacts';
    setJustAdded(`${action} ${name.trim()}`);
    resetAdultForm();
    if (saveToContacts && onRefreshContacts) onRefreshContacts();
    return true;
  };

  const handleAddAdultAndClose = async () => {
    const success = await handleAddAdult();
    if (success) onClose();
  };

  const handleAddHouseholdMember = async () => {
    setError('');
    setJustAdded(null);

    if (!selectedParent) { setError('Please select a parent/guardian contact first'); return false; }
    if (!hmFirstname.trim()) { setError('First name is required'); return false; }
    if (!hmBirthmonth) { setError('Birth month is required'); return false; }
    if (!hmBirthdate) { setError('Birth day is required'); return false; }

    setSaving(true);
    try {
      const resp = await api.post(`/contacts/${selectedParent.contactid}/household`, {
        firstname: hmFirstname.trim(),
        birthmonth: parseInt(hmBirthmonth),
        birthdate: parseInt(hmBirthdate),
        notes: hmNotes || null,
      });

      const member = resp.member;

      if (addAsRecipient && !atLimit) {
        const hmKey = `household-${member.memberid}`;
        onAdd({
          name: hmFirstname.trim(), email: selectedParent.email,
          contactid: selectedParent.contactid, householdMemberId: member.memberid,
          birthmonth: parseInt(hmBirthmonth), birthdate: parseInt(hmBirthdate),
          birthdayDisplay: formatBirthday(parseInt(hmBirthmonth), parseInt(hmBirthdate)),
          timezone: null, source: 'Household', isHouseholdMember: true, recipientKey: hmKey,
        });
      }

      setJustAdded(`Added ${hmFirstname.trim()} to ${selectedParent.name}'s household`);
      resetHouseholdForm();
      if (onRefreshContacts) onRefreshContacts();
      return true;
    } catch (err) {
      setError(err.data?.error || 'Failed to add household member');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAddHouseholdAndClose = async () => {
    const success = await handleAddHouseholdMember();
    if (success) onClose();
  };

  const handleCreateParentAndMember = async () => {
    setError('');
    if (!newParentName.trim()) { setError('Parent/guardian name is required'); return; }
    if (!newParentEmail.trim()) { setError('Parent/guardian email is required'); return; }
    const emailValid = typeof isValidEmail === 'function' ? isValidEmail(newParentEmail) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newParentEmail);
    if (!emailValid) { setError('Please enter a valid email'); return; }
    if (!hmFirstname.trim()) { setError("Child's first name is required"); return; }
    if (!hmBirthmonth) { setError("Child's birth month is required"); return; }
    if (!hmBirthdate) { setError("Child's birth day is required"); return; }

    setSaving(true);
    try {
      const nameParts = newParentName.trim().split(/\s+/);
      const firstname = nameParts[0];
      const lastname = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

      const parentResp = await api.post('/contacts', {
        email: newParentEmail.toLowerCase().trim(), firstname, lastname,
      });
      const parent = parentResp.contact;

      const hmResp = await api.post(`/contacts/${parent.contactid}/household`, {
        firstname: hmFirstname.trim(),
        birthmonth: parseInt(hmBirthmonth),
        birthdate: parseInt(hmBirthdate),
        notes: hmNotes || null,
      });
      const member = hmResp.member;

      if (addAsRecipient && !atLimit) {
        const hmKey = `household-${member.memberid}`;
        onAdd({
          name: hmFirstname.trim(), email: parent.email,
          contactid: parent.contactid, householdMemberId: member.memberid,
          birthmonth: parseInt(hmBirthmonth), birthdate: parseInt(hmBirthdate),
          birthdayDisplay: formatBirthday(parseInt(hmBirthmonth), parseInt(hmBirthdate)),
          timezone: null, source: 'Household', isHouseholdMember: true, recipientKey: hmKey,
        });
      }

      setJustAdded(`Created ${newParentName.trim()} and added ${hmFirstname.trim()} as household member`);
      setMode('household');
      setSelectedParent({ contactid: parent.contactid, name: newParentName.trim(), email: parent.email });
      resetHouseholdForm();
      setNewParentName('');
      setNewParentEmail('');
      if (onRefreshContacts) onRefreshContacts();
    } catch (err) {
      const errMsg = err.data?.error || 'Failed to create contact';
      if (errMsg.includes('already exists') || err.status === 400) {
        setError('A contact with this email already exists. Go back and search for them instead.');
      } else {
        setError(errMsg);
      }
    }
    setSaving(false);
  };

  const handleDuplicateAddHousehold = () => {
    setSelectedParent(duplicateContact);
    setDuplicateContact(null);
    setMode('household');
    resetAdultForm();
  };

  const handleDuplicateDismiss = () => {
    setDuplicateContact(null);
    setMode('adult');
    setError('A contact with this email already exists.');
  };

  const resetAdultForm = () => {
    setName(''); setEmail(''); setBirthmonth(''); setBirthdate(''); setTimezone('');
  };

  const resetHouseholdForm = () => {
    setHmFirstname(''); setHmBirthmonth(''); setHmBirthdate(''); setHmNotes('');
  };

  const modeLabel = mode === 'adult' ? 'Add Recipient' :
    mode === 'duplicate-prompt' ? 'Email Already Exists' :
    mode === 'household-new-parent' ? 'New Parent + Child' : 'Add Household Member';

  const modeSubtitle = mode === 'adult' ? 'Add someone to your Moonbeam' :
    mode === 'duplicate-prompt' ? '' :
    mode === 'household-new-parent' ? 'Create a parent contact and add a child' : 'Add a child or family member';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col sm:mx-4">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">{modeLabel}</h2>
            {modeSubtitle && <p className="text-royal-100 text-sm">{modeSubtitle}</p>}
          </div>
          <button onClick={onClose} className="text-white hover:text-gold transition-colors text-xl p-2">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">

          {(mode === 'adult' || mode === 'household') && (
            <div className="flex gap-2">
              <button onClick={() => { setMode('adult'); setError(''); setJustAdded(null); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  mode === 'adult' ? 'bg-gold text-royal-900' : 'bg-royal-800/50 text-royal-100 border border-royal-600 hover:text-gold'
                }`}>
                Adult / Contact
              </button>
              <button onClick={() => { setMode('household'); setError(''); setJustAdded(null); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  mode === 'household' ? 'bg-gold text-royal-900' : 'bg-royal-800/50 text-royal-100 border border-royal-600 hover:text-gold'
                }`}>
                Child / Household
              </button>
            </div>
          )}

          {(mode === 'adult' || mode === 'household') && (
            <div className="px-3 py-2 bg-royal-800/40 border border-royal-700/50 rounded-lg">
              <p className="text-royal-100 text-sm">
                You can also manage contacts and household members on your{' '}
                <a href="/contacts" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-light underline">Contacts page</a>
                {(() => {
                  const canGoogle = tier?.canimportgoogle;
                  const canCSV = tier?.canuploadcsv;
                  if (canGoogle || canCSV) {
                    const methods = [canGoogle && 'Google', canCSV && 'CSV'].filter(Boolean).join(' & ');
                    return <>, including bulk import via {methods}</>;
                  }
                  return null;
                })()}.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-900/20 border border-red-600/40 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {justAdded && (
            <div className="p-3 bg-green-900/20 border border-green-600/40 rounded-lg">
              <p className="text-green-400 text-sm">✓ {justAdded}. Add another or tap Done.</p>
            </div>
          )}

          {mode === 'duplicate-prompt' && duplicateContact && (
            <div className="space-y-4">
              <div className="p-4 bg-royal-800/40 border border-royal-600 rounded-xl text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-full bg-gold/10 flex items-center justify-center">
                  <span className="text-gold text-lg font-bold">{duplicateContact.name[0].toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-white font-medium text-base">{duplicateContact.name}</p>
                  <p className="text-royal-100 text-sm">{duplicateContact.email}</p>
                </div>
                <p className="text-royal-100 text-sm leading-relaxed">
                  This email is already in your contacts. Are you trying to add a household member (like a child) to this contact?
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={handleDuplicateDismiss}
                  className="flex-1 px-4 py-2.5 border border-royal-600 text-white rounded-lg text-sm hover:border-royal-200 transition-colors">
                  No, go back
                </button>
                <button onClick={handleDuplicateAddHousehold}
                  className="flex-1 px-4 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors">
                  Yes, add household member
                </button>
              </div>
            </div>
          )}

          {mode === 'adult' && (
            <>
              <div>
                <label className="block text-white text-sm mb-1.5">Name *</label>
                <input type="text" placeholder="Name (shown on their card)" value={name} onChange={(e) => setName(e.target.value)}
                  disabled={addAsRecipient && atLimit} autoFocus
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors disabled:opacity-60" />
              </div>

              <div>
                <label className="block text-white text-sm mb-1.5">Email *</label>
                <input type="email" placeholder="Their email address" value={email} onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAdult())}
                  disabled={addAsRecipient && atLimit}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors disabled:opacity-60" />
              </div>

              <div>
                <label className="block text-white text-sm mb-1.5">Birthday{isBirthdayDelivery ? ' *' : ''}</label>
                <div className="grid grid-cols-2 gap-2">
                  <select value={birthmonth}
                    onChange={(e) => { setBirthmonth(e.target.value); if (birthdate && parseInt(birthdate) > daysInMonth(parseInt(e.target.value))) setBirthdate(''); }}
                    className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                    {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <select value={birthdate} onChange={(e) => setBirthdate(e.target.value)}
                    className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                    <option value="">Day</option>
                    {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              {isBirthdayDelivery && (
                <div>
                  <label className="block text-white text-sm mb-1.5">Timezone</label>
                  <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                    <option value="">Optional</option>
                    {TIMEZONE_REGION_ORDER.map((region) => {
                      const tzs = timezoneGrouped[region];
                      if (!tzs?.length) return null;
                      return (
                        <optgroup key={region} label={region}>
                          {tzs.map((tz) => <option key={tz.tzcode} value={tz.tzcode}>{tz.label}</option>)}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              )}

              <div className="space-y-3 pt-2 border-t border-royal-700/50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={saveToContacts} onChange={(e) => setSaveToContacts(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-gold" />
                  <div>
                    <span className="text-white text-base">Save to my contacts</span>
                    <p className="text-royal-100 text-sm">Uncheck if this is a one-time send</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={addAsRecipient} onChange={(e) => setAddAsRecipient(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-gold" />
                  <div>
                    <span className="text-white text-base">Add as Moonbeam recipient</span>
                  </div>
                </label>
              </div>
            </>
          )}

          {mode === 'household' && (
            <>
              {!selectedParent ? (
                <div className="space-y-3">
                  <p className="text-royal-100 text-sm">First, choose the parent or guardian's contact:</p>
                  <div className="relative">
                    <input type="text" placeholder="Search contacts by name or email..." value={parentSearch}
                      onChange={(e) => setParentSearch(e.target.value)} autoFocus
                      className="w-full pl-10 pr-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                    <svg className="absolute left-3 top-3 w-5 h-5 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  {parentSearchLoading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-5 h-5 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
                    </div>
                  )}

                  {parentResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {parentResults.map((c) => {
                        const pName = [c.firstname, c.lastname].filter(Boolean).join(' ') || c.email;
                        return (
                          <button key={c.contactid}
                            onClick={() => { setSelectedParent({ contactid: c.contactid, name: pName, email: c.email }); setParentSearch(''); setParentResults([]); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-royal-800/40 transition-colors">
                            <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-gold text-sm font-bold">{pName[0].toUpperCase()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{pName}</p>
                              <p className="text-royal-100 text-sm truncate">{c.email}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {parentSearch.length >= 2 && !parentSearchLoading && parentResults.length === 0 && (
                    <p className="text-royal-100 text-sm text-center py-3">No contacts found</p>
                  )}

                  <button onClick={() => { setMode('household-new-parent'); setError(''); }}
                    className="w-full px-4 py-2.5 border border-royal-600 text-royal-100 rounded-lg text-sm hover:text-gold hover:border-gold/50 transition-colors">
                    + Parent not in contacts? Create new
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-royal-800/40 border border-royal-600 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-gold text-sm font-bold">{selectedParent.name[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{selectedParent.name}</p>
                      <p className="text-royal-100 text-sm truncate">{selectedParent.email}</p>
                    </div>
                    <button onClick={() => setSelectedParent(null)} className="text-royal-100 hover:text-white text-sm transition-colors">
                      Change
                    </button>
                  </div>

                  <div>
                    <label className="block text-white text-sm mb-1.5">Child's First Name *</label>
                    <input type="text" placeholder="First name only" value={hmFirstname}
                      onChange={(e) => setHmFirstname(e.target.value)} autoFocus
                      className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                  </div>

                  <div>
                    <label className="block text-white text-sm mb-1.5">
                      Birthday *
                      <span className="text-royal-100 font-normal ml-1">— for birthday auto send</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={hmBirthmonth}
                        onChange={(e) => { setHmBirthmonth(e.target.value); if (hmBirthdate && parseInt(hmBirthdate) > daysInMonth(parseInt(e.target.value))) setHmBirthdate(''); }}
                        className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                        {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                      <select value={hmBirthdate} onChange={(e) => setHmBirthdate(e.target.value)}
                        className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                        <option value="">Day</option>
                        {Array.from({ length: hmMaxDay }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm mb-1.5">Notes</label>
                    <input type="text" placeholder="Optional (e.g. son, daughter)" value={hmNotes}
                      onChange={(e) => setHmNotes(e.target.value)}
                      className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-royal-700/50">
                    <input type="checkbox" checked={addAsRecipient} onChange={(e) => setAddAsRecipient(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-gold" />
                    <span className="text-white text-sm">Add as Moonbeam recipient</span>
                  </label>
                </>
              )}
            </>
          )}

          {mode === 'household-new-parent' && (
            <div className="space-y-4">
              <button onClick={() => { setMode('household'); setError(''); }}
                className="text-royal-100 text-sm hover:text-gold transition-colors flex items-center gap-1">
                <span>←</span> <span>Back to search</span>
              </button>

              <div className="p-3 bg-royal-800/40 border border-royal-700/50 rounded-lg">
                <p className="text-gold text-sm font-medium mb-2">Parent / Guardian</p>
                <div className="space-y-3">
                  <input type="text" placeholder="Parent's name *" value={newParentName}
                    onChange={(e) => setNewParentName(e.target.value)} autoFocus
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                  <input type="email" placeholder="Parent's email address *" value={newParentEmail}
                    onChange={(e) => setNewParentEmail(e.target.value)}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                </div>
              </div>

              <div className="p-3 bg-royal-800/40 border border-royal-700/50 rounded-lg">
                <p className="text-gold text-sm font-medium mb-2">Child / Household Member</p>
                <div className="space-y-3">
                  <input type="text" placeholder="Child's first name *" value={hmFirstname}
                    onChange={(e) => setHmFirstname(e.target.value)}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                  <label className="block text-white text-sm mb-1.5">
                    Birthday *
                    <span className="text-royal-100 font-normal ml-1">— for birthday auto send</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={hmBirthmonth}
                      onChange={(e) => { setHmBirthmonth(e.target.value); if (hmBirthdate && parseInt(hmBirthdate) > daysInMonth(parseInt(e.target.value))) setHmBirthdate(''); }}
                      className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                      {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select value={hmBirthdate} onChange={(e) => setHmBirthdate(e.target.value)}
                      className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                      <option value="">Day</option>
                      {Array.from({ length: hmMaxDay }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <input type="text" placeholder="Notes (optional)" value={hmNotes}
                    onChange={(e) => setHmNotes(e.target.value)}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={addAsRecipient} onChange={(e) => setAddAsRecipient(e.target.checked)}
                  className="mt-0.5 w-5 h-5 accent-gold" />
                <span className="text-white text-sm">Add child as Moonbeam recipient</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 px-4 sm:px-6 py-4 border-t border-royal-700/50 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-white text-base hover:text-gold transition-colors">
            Done
          </button>

          {mode === 'adult' && (
            <div className="flex gap-2">
              <button onClick={handleAddAdult} disabled={atLimit || saving}
                className="px-4 py-2.5 border border-gold text-gold font-semibold rounded-lg text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
                {saving ? 'Saving...' : '+ Add Another'}
              </button>
              <button onClick={handleAddAdultAndClose} disabled={atLimit || saving}
                className="px-4 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors disabled:opacity-60">
                {saving ? 'Saving...' : 'Add & Close'}
              </button>
            </div>
          )}

          {mode === 'household' && selectedParent && (
            <div className="flex gap-2">
              <button onClick={handleAddHouseholdMember} disabled={saving}
                className="px-4 py-2.5 border border-gold text-gold font-semibold rounded-lg text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
                {saving ? 'Saving...' : '+ Add Another'}
              </button>
              <button onClick={handleAddHouseholdAndClose} disabled={saving}
                className="px-4 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors disabled:opacity-60">
                {saving ? 'Saving...' : 'Add & Close'}
              </button>
            </div>
          )}

          {mode === 'household-new-parent' && (
            <button onClick={handleCreateParentAndMember} disabled={saving}
              className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors disabled:opacity-60">
              {saving ? 'Saving...' : 'Create & Add'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Group Picker Modal ───────────────────────────────────────────────────────

function GroupPickerModal({ selectedKeys, onAddGroup, onRemoveGroupKeys, isBirthdayDelivery, addedGroups, setAddedGroups, onClose }) {
  const [types, setTypes] = useState([]);
  const [loadingType, setLoadingType] = useState(null);
  const loadingRef = useRef(false);
  const groupKeysRef = useRef({});

  useEffect(() => {
    contactService.getTypes()
      .then((d) => { const withContacts = (d.types || []).filter((t) => t.contactcount > 0); setTypes(withContacts); })
      .catch(() => {});
  }, []);

  const handleToggleGroup = async (type) => {
    const typeId = type.contacttypeid;
    if (loadingRef.current) return;

    if (addedGroups[typeId] !== undefined) {
      const keysToRemove = groupKeysRef.current[typeId] || [];
      onRemoveGroupKeys(keysToRemove);
      setAddedGroups((prev) => { const next = { ...prev }; delete next[typeId]; return next; });
      delete groupKeysRef.current[typeId];
      return;
    }

    loadingRef.current = true;
    setLoadingType(typeId);
    try {
      const data = await contactService.getContacts(`type=${typeId}&limit=1000`);
      const contacts = data.contacts || [];
      const groupRecipients = [];
      const groupKeys = [];

      for (const c of contacts) {
        const name = [c.firstname, c.lastname].filter(Boolean).join(' ') || c.email;
        const hasBirthday = !!(c.birthmonth && c.birthdate);
        if (isBirthdayDelivery && !hasBirthday) continue;

        const contactKey = `contact-${c.contactid}`;
        groupRecipients.push({
          name, email: c.email, contactid: c.contactid,
          birthmonth: c.birthmonth || null, birthdate: c.birthdate || null,
          birthdayDisplay: formatBirthday(c.birthmonth, c.birthdate),
          timezone: c.timezone || null, source: type.typename, recipientKey: contactKey,
        });
        groupKeys.push(contactKey);
      }

      groupKeysRef.current[typeId] = groupKeys;
      setAddedGroups((prev) => ({ ...prev, [typeId]: groupRecipients.length }));
      onAddGroup(groupRecipients);
    } catch (err) { console.error('[GroupPicker] Failed to load group contacts:', err); }
    setLoadingType(null);
    loadingRef.current = false;
  };

  const personalTypes = types.filter((t) => t.classname === 'Personal');
  const businessTypes = types.filter((t) => t.classname === 'Business');
  const allGroups = [
    { label: 'Personal', types: personalTypes },
    { label: 'Business', types: businessTypes },
  ].filter((g) => g.types.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col sm:mx-4">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">Add a Group</h2>
            <p className="text-royal-100 text-sm">Tap a group to add all its contacts</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gold transition-colors text-xl p-2">✕</button>
        </div>

        <div className="mx-4 sm:mx-6 mt-3 px-3 py-2.5 bg-royal-800/40 border border-royal-700/50 rounded-lg flex-shrink-0">
          <p className="text-royal-100 text-sm">
            <span className="text-gold font-medium">Note:</span> Household members aren't included in groups. Add them individually from contacts.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-3">
          {allGroups.length === 0 ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-royal-800/60 flex items-center justify-center">
                <svg className="w-7 h-7 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-white text-base">No groups with contacts found</p>
              <p className="text-royal-100 text-sm leading-relaxed max-w-sm mx-auto">
                Organize your contacts into groups on your{' '}
                <a href="/contacts" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-light underline">Contacts page</a>
                {' '}to send cards to multiple people at once — it makes sending much faster in the future!
              </p>

              <div className="text-left bg-royal-800/40 border border-royal-700/50 rounded-lg p-3 max-w-sm mx-auto">
                <p className="text-gold text-sm font-medium mb-1.5">Bulk import with CSV</p>
                <p className="text-royal-100 text-sm leading-relaxed mb-2">
                  You can also import hundreds or thousands of contacts at once using a CSV file and assign them to groups automatically.
                </p>
                <div className="text-royal-100 text-sm space-y-1">
                  <p>• Add a <span className="text-white">groups</span> column to your spreadsheet</p>
                  <p>• Separate multiple groups with semicolons (e.g., "Family;Friends")</p>
                  <p>• Group names must match your existing groups</p>
                </div>
                <a href="/contacts" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-gold text-sm hover:text-gold-light underline">
                  Go to Contacts to import →
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {allGroups.map((group) => (
                <div key={group.label}>
                  <p className="text-white text-sm font-semibold uppercase tracking-widest mb-2">{group.label}</p>
                  <div className="space-y-1.5">
                    {group.types.map((t) => {
                      const isAdded = addedGroups[t.contacttypeid] !== undefined;
                      const isLoading = loadingType === t.contacttypeid;
                      return (
                        <button key={t.contacttypeid} onClick={() => handleToggleGroup(t)} disabled={isLoading}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                            isAdded ? 'border-gold bg-gold/10' : 'border-royal-700 bg-royal-900/30 hover:border-royal-200'
                          }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isAdded ? 'bg-gold border-gold' : 'border-royal-600'}`}>
                              {isAdded && <svg className="w-3.5 h-3.5 text-royal-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <div className="text-left">
                              <span className={`text-base font-medium ${isAdded ? 'text-gold' : 'text-white'}`}>{t.typename}</span>
                              <span className="text-royal-100 text-sm ml-2">{t.contactcount} contact{t.contactcount !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isLoading && <div className="w-5 h-5 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />}
                            {isAdded && <span className="text-sm text-gold">{addedGroups[t.contacttypeid]} added</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {isBirthdayDelivery && (
            <p className="text-royal-100 text-sm mt-4">Contacts without a birthday will be excluded.</p>
          )}
        </div>

        <div className="flex justify-end px-4 sm:px-6 py-3 border-t border-royal-700/50 flex-shrink-0">
          <button onClick={onClose} className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shareable Link Section ───────────────────────────────────────────────────

function ShareableLinkSection({
  linkEnabled, setLinkEnabled,
  linkLabel, setLinkLabel,
  isLoggedIn,
}) {
  const [noteOpen, setNoteOpen] = useState(() => !!(linkLabel || '').trim());

  useEffect(() => {
    if (linkEnabled && (linkLabel || '').trim() && !noteOpen) {
      setNoteOpen(true);
    }
    if (!linkEnabled && noteOpen && !(linkLabel || '').trim()) {
      setNoteOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkEnabled, linkLabel]);

  const handleAddNote = () => setNoteOpen(true);

  const handleRemoveNote = () => {
    setLinkLabel('');
    setNoteOpen(false);
  };

  return (
    <div className="border-2 border-royal-600 rounded-xl bg-royal-900/40 overflow-hidden">
      <div className="px-4 sm:px-5 py-4">
        <label className={`flex items-start gap-3 ${isLoggedIn ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={linkEnabled}
              onChange={(e) => setLinkEnabled(e.target.checked)}
              disabled={!isLoggedIn}
              className="sr-only peer"
              aria-label="Get a link to share yourself"
            />
            <span
              className={`block w-[52px] h-7 rounded-full transition-colors duration-200
                bg-royal-700 peer-checked:bg-gold
                peer-disabled:bg-royal-700/40 peer-disabled:peer-checked:bg-gold/40
                peer-focus-visible:ring-2 peer-focus-visible:ring-gold peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-royal-900
                ${isLoggedIn ? '' : 'opacity-60'}`}
              aria-hidden="true"
            />
            <span
              className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                peer-checked:translate-x-6
                ${linkEnabled ? 'translate-x-6' : 'translate-x-0'}`}
              aria-hidden="true"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-base sm:text-lg">Get a link to share yourself</h3>
            <p className="text-royal-100 text-sm mt-0.5">
              Paste it in any app — text, WhatsApp, Facebook, Instagram, anywhere.
            </p>
          </div>
        </label>

        {linkEnabled && (
          <div className="pl-[68px] pt-4">
            {!noteOpen ? (
              <button
                type="button"
                onClick={handleAddNote}
                className="inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold-light transition-colors"
              >
                <span className="text-base leading-none">+</span>
                <span className="underline">Add a note</span>
              </button>
            ) : (
              <div>
                <label htmlFor="shareLinkNote" className="block text-white text-sm mb-1.5">
                  Note <span className="text-royal-100 font-normal">(for your reference)</span>
                </label>
                <input
                  id="shareLinkNote"
                  type="text"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="e.g., Grandkids"
                  maxLength={255}
                  autoFocus
                  className="w-full px-3 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors"
                />
                <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
                  <p className="text-royal-100 text-sm">
                    Examples: Grandkids · Facebook group · Family text
                  </p>
                  <button
                    type="button"
                    onClick={handleRemoveNote}
                    className="text-sm text-royal-100 hover:text-red-400 transition-colors underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── "OR also" Divider ────────────────────────────────────────────────────────

function OrAlsoDivider() {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold/30 to-gold/30" />
      <span className="text-gold text-sm font-semibold uppercase tracking-widest">or also</span>
      <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gold/30 to-gold/30" />
    </div>
  );
}

// ─── Simple Email Form ────────────────────────────────────────────────────────

function SimpleEmailForm({
  recipients, setRecipients,
  isBirthdayDelivery,
  fromName, setFromName, defaultFromName,
}) {
  const existing = recipients[0] || null;

  const [name, setName] = useState(existing?.name || '');
  const [email, setEmail] = useState(existing?.email || '');
  const [birthmonth, setBirthmonth] = useState(existing?.birthmonth ? String(existing.birthmonth) : '');
  const [birthdate, setBirthdate] = useState(existing?.birthdate ? String(existing.birthdate) : '');

  const maxDay = daysInMonth(birthmonth ? parseInt(birthmonth) : null);

  const trimmedEmail = email.trim().toLowerCase();
  const emailTouched = email.trim().length > 0;
  const emailValid = emailTouched && (typeof isValidEmail === 'function'
    ? isValidEmail(trimmedEmail)
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail));

  useEffect(() => {
    const bm = birthmonth ? parseInt(birthmonth) : null;
    const bd = birthdate ? parseInt(birthdate) : null;
    const nameOk = name.trim().length > 0;
    const birthdayOk = !isBirthdayDelivery || (!!bm && !!bd);

    if (nameOk && emailValid && birthdayOk) {
      setRecipients([{
        name: name.trim(),
        email: trimmedEmail,
        contactid: null,
        birthmonth: bm,
        birthdate: bd,
        birthdayDisplay: formatBirthday(bm, bd),
        timezone: null,
        source: 'Manual',
        recipientKey: 'simple-recipient',
      }]);
    } else {
      setRecipients([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, email, birthmonth, birthdate, isBirthdayDelivery]);

  const fromNameIsBlank = (fromName || '').trim().length === 0;

  return (
    <div className="space-y-4">
      {/* Send to — name + email */}
      <div className="border border-royal-600 rounded-xl bg-royal-900/30 p-4 space-y-3">
        <p className="text-white text-sm font-semibold uppercase tracking-wider">Send to</p>

        <div>
          <label htmlFor="simpleRecipientName" className="block text-white text-sm mb-1.5">Name</label>
          <input
            id="simpleRecipientName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="simpleRecipientEmail" className="block text-white text-sm mb-1.5">Email</label>
          <input
            id="simpleRecipientEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="their@email.com"
            className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors"
          />
          {emailTouched && !emailValid && (
            <p className="text-red-400 text-sm mt-1">Please enter a valid email address.</p>
          )}
        </div>

        {isBirthdayDelivery && (
          <div>
            <label className="block text-white text-sm mb-1.5">
              Their birthday <span className="text-royal-100 font-normal">— needed for birthday auto-send</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={birthmonth}
                onChange={(e) => {
                  setBirthmonth(e.target.value);
                  if (birthdate && parseInt(birthdate) > daysInMonth(parseInt(e.target.value))) setBirthdate('');
                }}
                className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
              >
                {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
              >
                <option value="">Day</option>
                {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* From — how the sender's name appears in the email */}
      <div className="border border-royal-600 rounded-xl bg-royal-900/30 p-4">
        <label htmlFor="simpleFromName" className="block text-white text-sm font-semibold uppercase tracking-wider mb-2">
          From
        </label>
        <p className="text-royal-100 text-sm mb-2 leading-relaxed">
          How your name appears in the email — like{' '}
          <span className="text-white">Grandma Jones</span> or{' '}
          <span className="text-white">Aunt Susie</span>.
        </p>
        <input
          id="simpleFromName"
          type="text"
          value={fromName || ''}
          onChange={(e) => setFromName(e.target.value)}
          placeholder={defaultFromName || 'Your name'}
          className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors"
        />
        {fromNameIsBlank && (
          <p className="text-royal-100 text-sm mt-2">
            Defaults to <span className="text-white">{defaultFromName || 'your account name'}</span> if left blank.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: How to Share (Main Step Component) ───────────────────────────────

export default function RecipientsAndTimingStep({
  recipients, setRecipients, deliveryMode, onDeliveryModeChange,
  scheduleDate, setScheduleDate, birthdayTime, setBirthdayTime,
  tier, isBirthdayTemplate, isBirthdayDelivery,
  canSend, isLoggedIn, usage, disableGroups, siteName,
  linkEnabled, setLinkEnabled, linkLabel, setLinkLabel,
  emailEnabled, setEmailEnabled,
  simpleEmailForm, fromName, setFromName, defaultFromName,
  sending, onNext, onShareDirect, onBack,
}) {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showBirthdayTimeModal, setShowBirthdayTimeModal] = useState(false);
  const [showRecipientsReview, setShowRecipientsReview] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [addedGroups, setAddedGroups] = useState({});

  const unlimited = isUnlimitedUsage(usage);
  const maxRecipients = !unlimited && usage ? usage.remaining + recipients.length : undefined;
  const atLimit = maxRecipients !== undefined && recipients.length >= maxRecipients;

  const selectedKeys = emailEnabled
    ? new Set(recipients.map((r) => r.recipientKey || `manual-${r.email}-${r.name}`))
    : new Set();

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: CONTACTS_PER_PAGE });
      if (search) params.set('search', search);
      const data = await contactService.getContacts(params.toString());
      setContacts(data.contacts);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { if (!simpleEmailForm) fetchContacts(); }, [fetchContacts, simpleEmailForm]);
  useEffect(() => { setPage(1); }, [search]);

  const addRecipient = (recipient) => {
    if (maxRecipients !== undefined && recipients.length >= maxRecipients) return false;
    const key = recipient.recipientKey || `manual-${recipient.email}-${recipient.name}`;
    if (selectedKeys.has(key)) return false;
    const updated = [...recipients, { ...recipient, recipientKey: key }];
    setRecipients(updated);
    return true;
  };

  const removeRecipient = (recipientKey) => {
    setRecipients(recipients.filter((r) => (r.recipientKey || `manual-${r.email}-${r.name}`) !== recipientKey));
  };

  const toggleContact = (recipient, action) => {
    if (action === 'add') addRecipient(recipient);
    else setRecipients(recipients.filter((r) => r.recipientKey !== recipient.recipientKey));
  };

  const addGroup = (groupRecipients) => {
    const newRecipients = groupRecipients.filter((r) => !selectedKeys.has(r.recipientKey));
    if (newRecipients.length) setRecipients([...recipients, ...newRecipients]);
  };

  const removeGroupKeys = (keys) => {
    const keySet = new Set(keys);
    setRecipients(recipients.filter((r) => !keySet.has(r.recipientKey)));
  };

  const clearAll = () => { setRecipients([]); setAddedGroups({}); };

  const hasConfiguredEmail = emailEnabled && recipients.length > 0;
  const hasConfiguredLink = !!linkEnabled;
  const canProceed = isLoggedIn && (hasConfiguredEmail || hasConfiguredLink);

  // ─── Share button: branch link-only vs email ────────────────────────────
  // LINK ONLY (link on, no email recipients) → run the real send now and
  // skip the review screen. Anything involving email → go to Step 2 first.
  const isLinkOnly = hasConfiguredLink && !hasConfiguredEmail;
  const shareButtonLabel = sending
    ? 'Sharing...'
    : isLinkOnly
      ? 'Share'
      : 'Share';
  const handleShareClick = () => {
    if (!canProceed || sending) return;
    if (isLinkOnly) {
      // Direct send — handleSend creates the card + link, copies it, and
      // navigates to My Moonbeams where the "link copied" modal appears.
      onShareDirect();
    } else {
      // Email is part of the share — go to the review screen first.
      onNext();
    }
  };

  const deliveryOptions = [
    { key: 'now', label: '⚡ Send Now', disabled: false },
    { key: 'schedule', label: '📅 Schedule Send', disabled: !tier?.canschedule },
  ];
  if (isBirthdayTemplate) {
    deliveryOptions.push({ key: 'birthday', label: '🎂 On Birthday', disabled: !tier?.autobirthday });
  }

  const [showDeliveryDropdown, setShowDeliveryDropdown] = useState(false);
  const deliveryDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (deliveryDropdownRef.current && !deliveryDropdownRef.current.contains(e.target)) {
        setShowDeliveryDropdown(false);
      }
    };
    if (showDeliveryDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDeliveryDropdown]);

  const currentDeliveryLabel = deliveryOptions.find(o => o.key === deliveryMode)?.label || '⚡ Send Now';

  const formatScheduleLabel = () => {
    if (!scheduleDate) return 'Pick date & time';
    return new Date(scheduleDate).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const formatBirthdayTimeLabel = () => {
    if (!birthdayTime) return 'Set delivery time';
    const [h, m] = birthdayTime.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h12}:${m} ${ampm}`;
  };

  const emailHeaderSummary = (() => {
    if (!emailEnabled) return 'Off — toggle on to add recipients';
    const count = recipients.length;
    const peopleStr = count === 0
      ? '0 recipients'
      : `${count} ${count === 1 ? 'person' : 'people'}`;
    let timing = 'Send now';
    if (deliveryMode === 'schedule') timing = scheduleDate ? 'Scheduled' : 'Schedule (set time)';
    else if (deliveryMode === 'birthday') timing = 'On birthday';
    return `${peopleStr} · ${timing}`;
  })();

  return (
    <div className="space-y-5">
      {!isLoggedIn && <UpgradeBanner isLoggedIn={false} message="Create an account to share Moonbeams" />}

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-white hover:text-gold transition-colors">
          <span>←</span><span>Back to Card</span>
        </button>
        <button
          onClick={handleShareClick}
          disabled={!canProceed || sending}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm sm:text-base hover:bg-gold-light transition-colors disabled:opacity-60 disabled:pointer-events-none"
        >
          <span>{shareButtonLabel}</span>
          {!sending && <span>→</span>}
        </button>
      </div>

      <p className="text-white text-sm sm:text-base">
        Choose how you want to share your card — pick one, or use both.
      </p>

      <div className={!isLoggedIn ? 'opacity-60 pointer-events-none space-y-5' : 'space-y-5'}>

        <ShareableLinkSection
          linkEnabled={linkEnabled}
          setLinkEnabled={setLinkEnabled}
          linkLabel={linkLabel}
          setLinkLabel={setLinkLabel}
          isLoggedIn={isLoggedIn}
        />

        <OrAlsoDivider />

        <div className="border-2 border-royal-600 rounded-xl bg-royal-900/40 overflow-hidden">
          <div className="px-4 sm:px-5 py-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                  className="sr-only peer"
                  aria-label={`Let ${siteName || 'The Cherry Fairy Shop'} email it for you`}
                />
                <span
                  className="block w-[52px] h-7 rounded-full transition-colors duration-200
                    bg-royal-700 peer-checked:bg-gold
                    peer-focus-visible:ring-2 peer-focus-visible:ring-gold peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-royal-900"
                  aria-hidden="true"
                />
                <span
                  className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                    peer-checked:translate-x-6
                    ${emailEnabled ? 'translate-x-6' : 'translate-x-0'}`}
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base sm:text-lg">
                  Let {siteName || 'The Cherry Fairy Shop'} email it for you
                </h3>
                <p className="text-royal-100 text-sm mt-0.5">
                  We send the card from our email system to the people you choose.
                </p>
                <p className={`text-sm mt-1 ${emailEnabled ? 'text-gold' : 'text-royal-100'}`}>
                  Status: {emailHeaderSummary}
                </p>
              </div>
            </label>
          </div>

          {emailEnabled && (
            <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-royal-700/40 space-y-4">

              {/* Spam "Heads up" notice — SHARED by both form modes. */}
              <div className="px-3 py-2.5 bg-royal-800/40 border border-royal-700/50 rounded-lg">
                <p className="text-royal-100 text-sm leading-relaxed">
                  <span className="text-gold font-medium">ℹ️ Heads up:</span> Email systems sometimes hide cards in spam. Tell recipients to check there if they don't see it. Sharing your own link above avoids this entirely.
                </p>
              </div>

              {/* "When" control — SHARED by both form modes. */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <label className="text-white text-sm font-medium whitespace-nowrap">When:</label>
                    <div className="relative flex-1" ref={deliveryDropdownRef}>
                      <button
                        onClick={() => setShowDeliveryDropdown(!showDeliveryDropdown)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-royal-900/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold/50 transition-colors"
                      >
                        <span>{currentDeliveryLabel}</span>
                        <svg className={`w-4 h-4 text-royal-100 transition-transform ${showDeliveryDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {showDeliveryDropdown && (
                        <div className="absolute z-40 left-0 right-0 top-full mt-1 bg-royal-900 border border-royal-600 rounded-lg shadow-xl shadow-black/40 overflow-hidden">
                          {deliveryOptions.map((opt) => (
                            <button
                              key={opt.key}
                              onClick={() => {
                                if (opt.disabled) return;
                                onDeliveryModeChange(opt.key);
                                setShowDeliveryDropdown(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-base transition-colors ${
                                opt.disabled
                                  ? 'text-royal-100 opacity-60 cursor-not-allowed'
                                  : opt.key === deliveryMode
                                  ? 'bg-gold/15 text-gold font-medium'
                                  : 'text-white hover:bg-royal-800/60'
                              }`}
                            >
                              <span>{opt.label}</span>
                              {opt.disabled && (
                                <span className="text-sm bg-royal-700 text-royal-100 px-2 py-0.5 rounded-full ml-2">Upgrade</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {deliveryMode === 'schedule' && (
                    <button onClick={() => setShowScheduleModal(true)}
                      className="px-3 py-2.5 bg-royal-900/50 border border-royal-600 rounded-lg text-white text-sm hover:border-gold/50 transition-colors whitespace-nowrap">
                      {formatScheduleLabel()}
                    </button>
                  )}

                  {deliveryMode === 'birthday' && (
                    <button onClick={() => setShowBirthdayTimeModal(true)}
                      className="px-3 py-2.5 bg-royal-900/50 border border-royal-600 rounded-lg text-white text-sm hover:border-gold/50 transition-colors whitespace-nowrap">
                      🕐 {formatBirthdayTimeLabel()}
                    </button>
                  )}
                </div>

                {isBirthdayDelivery && !simpleEmailForm && (
                  <p className="text-sm text-gold/80 mt-1.5 ml-1">Only contacts with birthdays can be selected</p>
                )}
              </div>

              {/* ─── EMAIL BODY: simple form vs. full recipient manager ─── */}
              {simpleEmailForm ? (
                <SimpleEmailForm
                  recipients={recipients}
                  setRecipients={setRecipients}
                  isBirthdayDelivery={isBirthdayDelivery}
                  fromName={fromName}
                  setFromName={setFromName}
                  defaultFromName={defaultFromName}
                />
              ) : (
                <>
                  {usage && !unlimited && (
                    <UsageStats usage={usage} isLifetime={usage.isLifetime} />
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <button onClick={() => setShowRecipientsReview(true)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gold/15 border border-gold/40 text-gold text-sm font-medium hover:bg-gold/25 transition-colors">
                        <span className="font-bold">{recipients.length}</span>
                        <span>Recipient{recipients.length !== 1 ? 's' : ''}</span>
                      </button>

                      <div className="flex items-center gap-2">
                        <button onClick={() => !disableGroups && setShowGroupPicker(true)} disabled={disableGroups || atLimit}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${
                            disableGroups ? 'border-royal-700 text-royal-100 opacity-60 cursor-not-allowed' :
                            atLimit ? 'border-royal-700 text-royal-100 opacity-60 cursor-not-allowed' :
                            'border-royal-600 text-white hover:text-gold hover:border-gold/50'
                          }`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="hidden sm:inline">Add Group{disableGroups ? ' 🔒' : ''}</span>
                          <span className="sm:hidden">{disableGroups ? '🔒' : 'Group'}</span>
                        </button>

                        <button onClick={() => setShowAddContact(true)} disabled={atLimit}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                            atLimit ? 'bg-royal-800 text-royal-100 opacity-60 cursor-not-allowed' : 'bg-gold text-royal-900 hover:bg-gold-light shadow-md shadow-gold/20'
                          }`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="hidden sm:inline">Add Contact</span>
                          <span className="sm:hidden">Add</span>
                        </button>
                      </div>
                    </div>

                    <p className="text-white text-sm">
                      Select recipients from your contacts below, or use the buttons above to add new contacts or groups.
                    </p>
                  </div>

                  <div className="border border-royal-600 rounded-xl bg-royal-900/30 overflow-hidden">
                    <div className="px-3 sm:px-4 py-3 border-b border-royal-700/30">
                      <div className="relative">
                        <input type="text" placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)}
                          className="w-full pl-10 pr-3 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-100 focus:outline-none focus:border-gold/50 transition-colors" />
                        <svg className="absolute left-3 top-3 w-5 h-5 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>

                    <div className="max-h-[50vh] overflow-y-auto">
                      {loading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
                        </div>
                      ) : contacts.length === 0 ? (
                        <div className="py-6 px-4">
                          {search ? (
                            <p className="text-royal-100 text-base text-center">No contacts match your search</p>
                          ) : (
                            <div className="text-center space-y-4">
                              <div className="w-14 h-14 mx-auto rounded-full bg-royal-800/60 flex items-center justify-center">
                                <svg className="w-7 h-7 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-white text-base font-medium mb-1">No contacts yet</p>
                                <p className="text-royal-100 text-sm leading-relaxed max-w-sm mx-auto">
                                  Get started by adding your first contact using the <span className="text-gold font-medium">+ Add Contact</span> button above — or head to your{' '}
                                  <a href="/contacts" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-light underline">Contacts page</a>
                                  {' '}to import many at once.
                                </p>
                              </div>

                              <div className="text-left bg-royal-800/40 border border-royal-700/50 rounded-lg p-3 max-w-sm mx-auto space-y-2">
                                <p className="text-gold text-sm font-medium">Ways to add contacts:</p>
                                <div className="text-royal-100 text-sm space-y-1.5">
                                  <p>✦ <span className="text-white">Add individually</span> — use the + Add Contact button to add one at a time right here</p>
                                  <p>✦ <span className="text-white">Import via CSV</span> — upload a spreadsheet with hundreds or thousands of contacts at once on the Contacts page</p>
                                  <p>✦ <span className="text-white">Google Contacts</span> — import directly from your Google account on the Contacts page</p>
                                </div>
                                <p className="text-royal-100 text-sm pt-1 border-t border-royal-700/30">
                                  Tip: Add a "groups" column in your CSV (e.g., "Family;Friends") to organize contacts into groups for faster sending in the future.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        contacts.map((c) => {
                          const name = [c.firstname, c.lastname].filter(Boolean).join(' ');
                          const displayName = name || c.email;
                          const contactKey = `contact-${c.contactid}`;
                          const isSelected = selectedKeys.has(contactKey);
                          const hasBirthday = !!(c.birthmonth && c.birthdate);
                          const isUnsubscribed = !!(c.isunsubscribed);
                          const disabled = isUnsubscribed || (isBirthdayDelivery && !hasBirthday) || (atLimit && !isSelected);
                          const householdMembers = c.household_members || [];

                          return (
                            <div key={c.contactid} className="border-b border-royal-800/40 last:border-0">
                              <button
                                onClick={() => {
                                  if (disabled) return;
                                  const recipient = {
                                    name: name || c.email, email: c.email, contactid: c.contactid,
                                    birthmonth: c.birthmonth || null, birthdate: c.birthdate || null,
                                    birthdayDisplay: formatBirthday(c.birthmonth, c.birthdate),
                                    timezone: c.timezone || null, source: 'Contact', recipientKey: contactKey,
                                  };
                                  toggleContact(recipient, isSelected ? 'remove' : 'add');
                                }}
                                disabled={disabled}
                                className={`w-full flex items-center gap-3 px-3 sm:px-4 py-3 text-left transition-colors ${
                                  disabled ? 'opacity-60 cursor-not-allowed' : isSelected ? 'bg-gold/5' : 'hover:bg-royal-800/40'
                                }`}>
                                <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'bg-gold border-gold' : 'border-royal-600'}`}>
                                  {isSelected && <svg className="w-3.5 h-3.5 text-royal-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                </div>
                                <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-gold text-sm font-bold">{displayName[0].toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-base truncate ${isSelected ? 'text-gold font-medium' : 'text-white'}`}>
                                    {displayName}
                                  </p>
                                  <p className="text-royal-100 text-sm truncate">
                                    {name ? c.email : ''}
                                    {hasBirthday && <span className="ml-1">🎂 {formatBirthday(c.birthmonth, c.birthdate)}</span>}
                                  </p>
                                </div>
                                {isBirthdayDelivery && !hasBirthday && <span className="text-royal-100 text-sm italic flex-shrink-0">No birthday</span>}
                                {isUnsubscribed && <span className="text-red-400 text-sm italic flex-shrink-0">Unsubscribed</span>}
                              </button>

                              {householdMembers.map((hm) => {
                                const hmKey = `household-${hm.memberid}`;
                                const hmSelected = selectedKeys.has(hmKey);
                                const hmHasBirthday = !!(hm.birthmonth && hm.birthdate);
                                const hmDisabled = isUnsubscribed || (isBirthdayDelivery && !hmHasBirthday) || (atLimit && !hmSelected);

                                return (
                                  <button key={hm.memberid}
                                    onClick={() => {
                                      if (hmDisabled) return;
                                      const recipient = {
                                        name: hm.firstname, email: c.email, contactid: c.contactid,
                                        householdMemberId: hm.memberid, birthmonth: hm.birthmonth, birthdate: hm.birthdate,
                                        birthdayDisplay: formatBirthday(hm.birthmonth, hm.birthdate),
                                        timezone: c.timezone || null, source: 'Household', isHouseholdMember: true, recipientKey: hmKey,
                                      };
                                      toggleContact(recipient, hmSelected ? 'remove' : 'add');
                                    }}
                                    disabled={hmDisabled}
                                    className={`w-full flex items-center gap-3 pl-10 sm:pl-12 pr-3 py-2 text-left transition-colors border-l-2 border-l-gold/20 ml-6 ${
                                      hmDisabled ? 'opacity-60 cursor-not-allowed' : hmSelected ? 'bg-gold/5' : 'hover:bg-royal-800/30'
                                    }`}>
                                    <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${hmSelected ? 'bg-gold border-gold' : 'border-royal-600'}`}>
                                      {hmSelected && <svg className="w-3.5 h-3.5 text-royal-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                    </div>
                                    <div className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                      <span className="text-gold text-sm font-bold">{hm.firstname[0].toUpperCase()}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm truncate ${hmSelected ? 'text-gold' : 'text-white'}`}>{hm.firstname}</p>
                                      <p className="text-royal-100 text-sm truncate">via {displayName}{hmHasBirthday && <span className="ml-1">🎂 {formatBirthday(hm.birthmonth, hm.birthdate)}</span>}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-royal-700/50">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="text-sm text-white hover:text-gold disabled:opacity-60 transition-colors">← Prev</button>
                        <span className="text-royal-100 text-sm">Page {page} / {totalPages}</span>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="text-sm text-white hover:text-gold disabled:opacity-60 transition-colors">Next →</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showScheduleModal && (
        <ScheduleSendModal
          scheduleDate={scheduleDate}
          onScheduleDateChange={setScheduleDate}
          onClose={() => setShowScheduleModal(false)}
        />
      )}

      {showBirthdayTimeModal && (
        <BirthdayTimeModal
          birthdayTime={birthdayTime}
          onBirthdayTimeChange={setBirthdayTime}
          onClose={() => setShowBirthdayTimeModal(false)}
        />
      )}

      {showRecipientsReview && (
        <RecipientsReviewModal
          recipients={recipients}
          onRemove={removeRecipient}
          onClearAll={clearAll}
          isBirthdayDelivery={isBirthdayDelivery}
          onClose={() => setShowRecipientsReview(false)}
        />
      )}

      {showAddContact && (
        <AddContactModal
          onAdd={addRecipient}
          isBirthdayDelivery={isBirthdayDelivery}
          atLimit={atLimit}
          onClose={() => setShowAddContact(false)}
          onRefreshContacts={fetchContacts}
          tier={tier}
        />
      )}

      {showGroupPicker && (
        <GroupPickerModal
          selectedKeys={selectedKeys}
          onAddGroup={addGroup}
          onRemoveGroupKeys={removeGroupKeys}
          isBirthdayDelivery={isBirthdayDelivery}
          addedGroups={addedGroups}
          setAddedGroups={setAddedGroups}
          onClose={() => setShowGroupPicker(false)}
        />
      )}
    </div>
  );
}